import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "398",
  "slug": "why-are-some-countries-long-and-thin",
  "title": "Why Are Some Countries So Long and Thin?",
  "excerpt": "Chile stretches more than 4,000 kilometres but averages under 200 wide. Why do some countries end up as long ribbons? Mountains, rivers, and history all play a part.",
  "date": "2026-06-10",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1709483076703-dd4eff1206e1?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhbmRlcyUyMG1vdW50YWlucyUyMGNvYXN0fGVufDF8MHx8fDE3ODEwNzkyMjB8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "Long, thin countries are usually shaped by physical barriers, a mountain range or coastline boxing them in, or by borders drawn along rivers and colonial lines. Chile, Norway, Vietnam, and The Gambia are the classic examples.",
  "content": [
    {
      "type": "paragraph",
      "text": "Some countries are tidy blobs. Others are stretched into impossibly long ribbons, hugging a coast or threading between mountains for thousands of kilometres. Chile runs more than 4,000 kilometres from top to bottom yet averages under 200 kilometres wide. Why does a country end up shaped like a noodle? The answers usually come down to a tug-of-war between physical geography and human history."
    },
    {
      "type": "heading",
      "text": "When Mountains and Oceans Do the Squeezing: Chile"
    },
    {
      "type": "paragraph",
      "text": "Chile is the textbook case. It is pinned between the towering wall of the Andes to the east and the Pacific Ocean to the west, with almost no room to grow sideways. As the country expanded, it had nowhere to go but along the narrow strip of habitable land between mountain and sea. The result is a nation that spans deserts, vineyards, forests, and glaciers, simply because it is long enough to cross that many climate zones."
    },
    {
      "type": "heading",
      "text": "A Coastline Too Rugged to Ignore: Norway"
    },
    {
      "type": "paragraph",
      "text": "Norway is long for a different reason: its coastline is one of the most jagged on Earth, carved into countless fjords by ancient glaciers. Mountains cover much of the interior, so settlement clung to the coast, stretching the country far up toward the Arctic. Measured along all its fjords and islands, Norway's coastline is staggeringly long for such a modest land area."
    },
    {
      "type": "heading",
      "text": "Following the Coast and the Rivers: Vietnam"
    },
    {
      "type": "paragraph",
      "text": "Vietnam curls down the eastern edge of mainland Southeast Asia in a long S-shape, bracketed by the sea on one side and highlands and neighbouring states on the other. Its population and farmland concentrate in two great river deltas, the Red River in the north and the Mekong in the south, with a slender corridor of coast linking them. The shape is the product of geography channelling people into the fertile, low-lying strip along the water."
    },
    {
      "type": "heading",
      "text": "When a Border Follows a River: The Gambia"
    },
    {
      "type": "paragraph",
      "text": "The Gambia is perhaps the most extreme example, a thin sliver of a country that follows the Gambia River inland and is almost entirely surrounded by Senegal. Its odd shape is a colonial inheritance: the boundary was drawn around the navigable river rather than around any natural region, leaving a country that is essentially a riverbank with borders."
    },
    {
      "type": "heading",
      "text": "Why Shape Matters"
    },
    {
      "type": "paragraph",
      "text": "A country's shape is not just trivia. Long, thin nations face real challenges in transport and defence, since roads, railways, and supply lines must stretch enormous distances. But there is an upside: spanning many degrees of latitude or elevation often means spanning many climates, giving a country remarkable variety in landscapes, crops, and culture along its length."
    },
    {
      "type": "list",
      "items": [
        "Physical barriers like the Andes box a country into a narrow strip",
        "Rugged, glacier-carved coastlines stretch settlement along the shore",
        "Fertile river valleys and deltas concentrate people in a corridor",
        "Borders drawn along rivers or colonial lines can ignore natural shape"
      ]
    }
  ]
};
